import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type SalesWindowContext } from '@components/admission-sales-dates';
import { CHANNEL_QUOTA_PER_CHANNEL } from '@components/distribution-section';
import { validateTeamChannels, type ValidationError } from '@components/team-channel-section';
import { type SupportedLanguages } from '@constants/constants.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { type Status } from '@domain/admission';
import { type EventTeam } from '@domain/eventTeam';
import {
  getTicketsLeftThresholdByType,
  useGetCompanyTicketsLeftThreshold,
} from '@hooks/company/use-get-company-tickets-left-threshold';
import { useGetCompanyVatRates } from '@hooks/company/use-get-company-vat-rates';
import { useGetCompanyClubChannels } from '@hooks/company-club/use-get-company-channels';
import { useGalleryImages } from '@hooks/gallery/use-gallery-images';
import { useGetPresetActiveChannels } from '@hooks/presets/admissions/use-get-admission-preset-channels';
import { useGetFormFieldsPresets } from '@hooks/use-form-fields-presets/use-form-fields-presets';
import { filterTreeToSelected } from '@utils/channel-tree';
import moment from 'moment-timezone';

import { seedChannelLimit } from './mappers/from-backend';
import { toPresetFormValue } from './mappers/from-preset-backend';
import { diffPresetRequest, toCreatePresetRequest } from './mappers/to-preset-backend';
import { resolveInitialVatRate } from './mappers/vat-rate';
import { type AdmissionFormValue, type PresetFormProps, type SectionKey } from './admission-form.type';
import { ADMISSION_FORM_FLOW_PRESET, ADMISSION_FORM_MODE_EDIT } from './constants';
import {
  ACCESS_CODE_PATTERN,
  buildOfferEditBaseline,
  isOfferSaveDisabled,
  isOfferSeedPending,
  seedOfferFormFields,
  toEventTeamChannel,
} from './offer-form-common';
import { getFormTitle, getSubmitLabel } from './section-header';
import { type UseAdmissionFormResult } from './use-admission-form';
import {
  getDefaultMaxItemsPerTransaction,
  isAdmissionFormValid,
  selectVisibleErrors,
  validateAdmissionForm,
  validateChannelLimit,
} from './validation';
import { getVisibleSections } from './visibility';

export const usePresetForm = ({
  admissionType,
  mode,
  preset,
  initialData,
  onSave: onSaveProp,
  onClose: onCloseProp,
  onDirtyChange,
  galleryController: galleryControllerProp,
}: PresetFormProps): UseAdmissionFormResult => {
  const { t, i18n } = useTranslation('offers');
  const { clubState, companyState, companyClubState, isExtraGuestsForBottleServiceActive } =
    useContext(CompanyClubContext);
  const { userState } = useContext(UserContext);
  const { isTicketsLeftThresholdEnabled } = useContext(FeatureFlagContext);

  const mainLanguage = (i18n.language?.split('-')[0] ?? 'en') as SupportedLanguages;
  const timezone = clubState.city.timezone;
  const currency = clubState.city.country.currency;
  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isSuperAdmin = userState?.isSuperAdmin ?? false;
  const hasItalyRegulationContext = isClubCountryItaly;
  const presetType = admissionType;

  const minValidFromDate = useMemo(() => moment.tz(timezone), [timezone]);
  const maxEndDate = useMemo(() => moment.tz(timezone).add(1, 'year'), [timezone]);

  const visibleSections = useMemo(
    () =>
      getVisibleSections({
        flow: ADMISSION_FORM_FLOW_PRESET,
        type: admissionType,
        isClubCountryItaly,
        hasItalyRegulationContext,
        isSuperAdmin,
        canExcludeSiaeOffers: false,
        isEventScoped: false,
        isEditMode: mode === ADMISSION_FORM_MODE_EDIT,
      }),
    [admissionType, isClubCountryItaly, hasItalyRegulationContext, isSuperAdmin, mode]
  );

  const defaultValues = useMemo<AdmissionFormValue>(
    () =>
      initialData ??
      toPresetFormValue({
        type: presetType,
        preset,
        mainLanguage,
        isSuperAdmin,
        canExcludeSiaeOffers: false,
        hasItalyRegulationContext,
        isTicketsLeftThresholdEnabled,
        isExtraGuestsForBottleServiceEnabled: isExtraGuestsForBottleServiceActive,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const methods = useForm<AdmissionFormValue>({ defaultValues, mode: 'onChange' });
  const hasUnsavedChanges = Object.keys(methods.formState.dirtyFields).length > 0;

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const { data: companyChannels } = useGetCompanyClubChannels({ companyClubId: companyClubState.id });
  const { data: activeChannels, isFetching: isActiveChannelsFetching } = useGetPresetActiveChannels({
    presetId: preset?.id,
    enabled: !!preset?.id,
  });
  const { data: formFieldPresetsData } = useGetFormFieldsPresets();
  const { data: companyVatRates } = useGetCompanyVatRates({
    companyId: companyState.id,
    options: { enabled: !!companyState.id && !isClubCountryItaly },
  });
  const { data: companyTicketsLeftThreshold } = useGetCompanyTicketsLeftThreshold({
    companyId: companyState.id,
    options: { enabled: !!companyState.id },
  });

  const bookingPresets = useMemo(() => formFieldPresetsData?.bookingFormFieldPresets ?? [], [formFieldPresetsData]);

  const presetChannelTeam = useMemo<EventTeam | undefined>(
    () =>
      companyChannels
        ? {
            teams: [],
            channelsWithoutTeams: companyChannels.filter((c) => c.isEnabled).map((c) => toEventTeamChannel(c.channel)),
          }
        : undefined,
    [companyChannels]
  );

  const internalGalleryController = useGalleryImages({
    entityId: preset?.id ?? null,
    initialImages: preset?.images ?? [],
    uploadImage: () => Promise.reject(new Error('Preset gallery persistence is wired in the drawer (B2B-1702)')),
    deleteImage: () => Promise.resolve(),
    reorderImages: () => Promise.resolve(),
  });
  const galleryController = galleryControllerProp ?? internalGalleryController;

  useEffect(() => {
    if (!isClubCountryItaly && !preset && !initialData && companyVatRates) {
      methods.setValue('vatRate.vatRate', resolveInitialVatRate({ companyVatRates, type: presetType, offer: null }), {
        shouldDirty: false,
      });
    }
  }, [companyVatRates, preset, initialData, methods, isClubCountryItaly, presetType]);

  useEffect(() => {
    if (
      !presetChannelTeam ||
      (preset?.id && (!activeChannels || isActiveChannelsFetching)) ||
      methods.getValues('distribution.channelLimit')
    )
      return;
    methods.setValue(
      'distribution.channelLimit',
      seedChannelLimit(presetChannelTeam, preset, undefined, activeChannels),
      { shouldDirty: false }
    );
  }, [presetChannelTeam, activeChannels, isActiveChannelsFetching, preset, methods]);

  const formFieldsSeededRef = useRef(false);
  useEffect(() => {
    if (formFieldsSeededRef.current || initialData || !formFieldPresetsData) return;
    formFieldsSeededRef.current = true;
    methods.setValue('formFields', seedOfferFormFields(preset, bookingPresets, false), { shouldDirty: false });
  }, [formFieldPresetsData, preset, initialData, bookingPresets, methods]);

  const [openSection, setOpenSection] = useState<SectionKey | null>(visibleSections[0] ?? null);
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
  }, []);

  const watchedValues = methods.watch();
  const allErrors = validateAdmissionForm({
    value: watchedValues,
    type: admissionType,
    mainLanguage,
    eventStartingTime: 0,
    eventEndingTime: 0,
    timezone,
    t,
  });
  const errors = selectVisibleErrors(allErrors, watchedValues, defaultValues);

  const channelLimit = watchedValues.distribution.channelLimit;
  const channelQuantity = watchedValues.basicInfo.quantity ? Number(watchedValues.basicInfo.quantity) : null;
  const channelErrors: ValidationError[] | undefined =
    channelLimit?.enabled && channelLimit.quotaMode === CHANNEL_QUOTA_PER_CHANNEL
      ? validateTeamChannels(
          filterTreeToSelected(channelLimit.channels, channelLimit.selectedChannelIds),
          channelQuantity
        )
      : undefined;
  const channelLimitError = validateChannelLimit(channelLimit, channelQuantity, t);

  const accessCode = watchedValues.distribution.accessCode;
  const accessCodeError = !!accessCode?.enabled && accessCode.code !== '' && !ACCESS_CODE_PATTERN.test(accessCode.code);

  const isSeedPending =
    isOfferSeedPending(formFieldPresetsData, presetChannelTeam, preset, activeChannels) || isActiveChannelsFetching;
  const isSaveDisabled = isOfferSaveDisabled({
    isSeedPending,
    allErrors,
    channelErrors,
    channelLimitError,
    accessCodeError,
    formFields: watchedValues.formFields,
  });

  const buildRequest = useCallback(
    (value: AdmissionFormValue) =>
      toCreatePresetRequest({
        value,
        type: presetType,
        currency,
        mainLanguage,
        companyClubId: companyClubState.id,
        preset,
        defaultTicketsLeftThreshold: getTicketsLeftThresholdByType(companyTicketsLeftThreshold, admissionType),
        formFieldPresets: bookingPresets,
      }),
    [
      presetType,
      currency,
      mainLanguage,
      companyClubState.id,
      preset,
      companyTicketsLeftThreshold,
      admissionType,
      bookingPresets,
    ]
  );

  const onSave = useCallback((): boolean => {
    if (isSaveDisabled) return false;
    const value = methods.getValues();
    if (
      !isAdmissionFormValid(
        validateAdmissionForm({
          value,
          type: admissionType,
          mainLanguage,
          eventStartingTime: 0,
          eventEndingTime: 0,
          timezone,
          t,
        })
      )
    ) {
      return false;
    }

    const fullPayload = buildRequest(value);
    const payload =
      mode === ADMISSION_FORM_MODE_EDIT && preset
        ? diffPresetRequest(
            fullPayload,
            buildRequest(
              buildOfferEditBaseline({
                defaultValues,
                initialData,
                channelTeam: presetChannelTeam,
                offer: preset,
                activeChannels,
                bookingPresets,
                defaultIsNominative: false,
              })
            )
          )
        : fullPayload;

    void onSaveProp(payload);
    return true;
  }, [
    isSaveDisabled,
    methods,
    admissionType,
    mainLanguage,
    t,
    timezone,
    buildRequest,
    mode,
    preset,
    defaultValues,
    initialData,
    presetChannelTeam,
    activeChannels,
    bookingPresets,
    onSaveProp,
  ]);

  const handleStatusChange = useCallback((newStatus: Status, apply: (status: Status) => void) => apply(newStatus), []);

  const salesWindowContext = useMemo<SalesWindowContext>(
    () => ({
      now: moment.tz(timezone),
      timezone,
      locale: mainLanguage,
      eventId: '',
      eventStartingTime: 0,
      eventEndingTime: 0,
      maxEndDate,
      eventIsInThePast: false,
      offerType: admissionType,
      offerId: preset?.id,
      selectedOfferTrigger: null,
      setSelectedOfferTrigger: () => undefined,
      onTriggerOfferCancel: () => undefined,
    }),
    [timezone, mainLanguage, maxEndDate, admissionType, preset?.id]
  );

  return {
    methods,
    title: getFormTitle(t, mode, ADMISSION_FORM_FLOW_PRESET, admissionType),
    maxItemsPlaceholder: getDefaultMaxItemsPerTransaction(admissionType),
    submitLabel: getSubmitLabel(t, mode),
    visibleSections,
    openSection,
    toggleSection,
    errors,
    isSaveDisabled,
    hasUnsavedChanges,
    isLoading: false,
    onSave,
    onClose: onCloseProp,
    handleStatusChange,
    currency,
    mainLanguage,
    timezone,
    availableAddOns: [],
    addOnsLoading: false,
    onOpenPresetsModal: () => undefined,
    isAddOnsPresetsModalOpen: false,
    closeAddOnsPresetsModal: () => undefined,
    onAddOnsPresetsSaved: () => undefined,
    eventId: '',
    companyClubId: companyClubState.id,
    addOnsHiddenReason: undefined,
    isAddOnsSectionHidden: true,
    formFieldPresets: bookingPresets,
    isPhoneLockedRequired: false,
    galleryController,
    floorplanImageUrl: undefined,
    salesWindowContext,
    minValidFromDate,
    maxEndDate,
    channelErrors,
    channelLimitError,
    channelLimitLoading: isSeedPending,
    accessCodeError,
    isEventInThePast: false,
    isPricingLocked: false,
    isDownpaymentLocked: false,
    isFormFieldsLocked: false,
    isEditingLocked: false,
    statusLock: { blocked: false, reasonKey: '' },
    showStatusControl: false,
    // Presets are never SIAE-approved (the preset payload carries only dirittoPrevendita), so the
    // "Approved" status stays disabled.
    isSiaeApprovalAllowed: false,
    onOpenStartAfter: undefined,
  };
};
